/**
 * Post-processing rig — bloom + FXAA + image processing on the main camera,
 * plus a GlowLayer for emissives (banners, faction crystals, ability VFX).
 *
 * Cheap-but-pretty: no SSAO (kills mobile), no SSR (overkill for stylised
 * art). Just enough to make the scene feel like a game and not a tech demo.
 */

import { Color3, GlowLayer, Scene } from '@babylonjs/core';
import { DefaultRenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline.js';

export interface PostFXRig {
  pipeline: DefaultRenderingPipeline;
  glow: GlowLayer;
  dispose: () => void;
}

export function installPostFX(scene: Scene): PostFXRig {
  // GlowLayer — emissive materials get a soft halo. Blur kernel small to
  // stay cheap on integrated GPUs. Picks up any material with emissiveColor
  // > 0 automatically.
  const glow = new GlowLayer('glow', scene, { blurKernelSize: 24 });
  glow.intensity = 0.6;

  // Bloom + FXAA + tone mapping pipeline. All three at once is one pass on
  // most GPUs — the marginal cost of FXAA on top of the others is tiny.
  const pipeline = new DefaultRenderingPipeline('aiwari-post', true, scene, scene.cameras);
  // R232: ALIVE = чёткая картинка. Юзер: "в живом мире чтобы было чётко".
  // Раньше vignette + heavy bloom + soft contrast = всё мутно.
  pipeline.fxaaEnabled = true;
  // R232q5cn: respect 'bloomEnabled' setting at init.
  pipeline.bloomEnabled = readBloomEnabled();
  pipeline.bloomThreshold = 0.92;  // 0.85 → 0.92 (только яркие пиксели)
  pipeline.bloomWeight = 0.15;     // 0.35 → 0.15 (мягкий нимб, не «дымка»)
  pipeline.bloomKernel = 24;       // 48 → 24 (резче)
  pipeline.bloomScale = 0.5;
  // R232q86z7: configure neutral image-processing knobs FIRST, THEN turn the
  // post-process off. setDeadModeFx tries to enable IP later — if we left it
  // null'd, that path crashes with "Cannot set properties of null". By writing
  // the neutral defaults up front the object exists, even if the pipeline's
  // own pass is bypassed.
  // R232q86zX: null-guard. On some user GPUs the DefaultRenderingPipeline's
  // imageProcessing post-process is lazy and stays null until first render —
  // touching it now throws «Cannot set properties of null (setting
  // 'vignetteEnabled')» and breaks the entire render loop. Skip silently;
  // setDeadModeFx already re-checks before touching the same fields.
  if (pipeline.imageProcessing) {
    pipeline.imageProcessing.toneMappingEnabled = false;
    pipeline.imageProcessing.exposure = 1.0;
    pipeline.imageProcessing.contrast = 1.0;
    pipeline.imageProcessing.vignetteEnabled = false;
    pipeline.imageProcessing.colorCurvesEnabled = false;
    pipeline.imageProcessing.colorGradingEnabled = false;
  }
  // R232q86zat: re-enable imageProcessing — proper gamma correction (linear
  // → sRGB) makes character peach/skin look natural instead of overbright.
  // Scene lights были уменьшены в q86zas (sun 0.6, hemi 0.35) so total
  // brightness is balanced без overcompensation.
  pipeline.imageProcessingEnabled = true;
  void Color3;

  // R232: expose pipeline globally чтобы World.tsx мог toggle dead-state blur.
  (window as unknown as { __aiwariPipeline?: DefaultRenderingPipeline }).__aiwariPipeline = pipeline;

  // R232q5cn: live-toggle bloom через Settings panel.
  const onSettings = (e: Event) => {
    const ev = e as CustomEvent<{ settings: { bloomEnabled?: boolean } }>;
    const v = ev.detail?.settings?.bloomEnabled;
    if (typeof v === 'boolean') pipeline.bloomEnabled = v;
  };
  window.addEventListener('aiwari-settings-changed', onSettings as EventListener);

  return {
    pipeline,
    glow,
    dispose: () => {
      window.removeEventListener('aiwari-settings-changed', onSettings as EventListener);
      pipeline.dispose();
      glow.dispose();
      delete (window as unknown as { __aiwariPipeline?: DefaultRenderingPipeline }).__aiwariPipeline;
    },
  };
}

function readBloomEnabled(): boolean {
  try {
    const raw = localStorage.getItem('aiwari-settings');
    if (!raw) return true;
    const s = JSON.parse(raw) as { bloomEnabled?: boolean };
    return s.bloomEnabled !== false;
  } catch { return true; }
}

/** R232: dead-state spirit-realm look — heavy vignette + desaturate + bloom.
 *  R232q5dr: bloomKernel change triggers GPU shader recompile (= freeze).
 *  Keep bloomKernel CONSTANT, only toggle vignette + exposure/contrast. */
export function setDeadModeFx(dead: boolean): void {
  const pipeline = (window as unknown as { __aiwariPipeline?: DefaultRenderingPipeline }).__aiwariPipeline;
  if (!pipeline || !pipeline.imageProcessing) return;
  // Dead-mode requires the IP pass — re-enable it so the vignette renders.
  // Live-mode keeps it off so default gamma doesn't darken the scene.
  pipeline.imageProcessingEnabled = dead;
  if (dead) {
    pipeline.imageProcessing.vignetteEnabled = true;
    pipeline.imageProcessing.vignetteWeight = 1.4;
    pipeline.imageProcessing.vignetteStretch = 1.2;
    pipeline.imageProcessing.vignetteColor = new Color3(0.05, 0.10, 0.20).toColor4(1);
    pipeline.imageProcessing.exposure = 0.85;
    pipeline.imageProcessing.contrast = 0.85;
  } else {
    pipeline.imageProcessing.vignetteEnabled = false;
    pipeline.imageProcessing.exposure = 1.0;
    pipeline.imageProcessing.contrast = 1.0;
  }
}
